import { useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useApiQuery } from '../api/useApiQuery';
import type { SearchResult } from '../api/types';
import { SearchResults } from '../components/SearchResults';
import { SearchScopeToggle } from '../components/SearchScopeToggle';
import { writeSearchScope } from '../lib/searchScope';
import type { SearchScope } from '../lib/searchScope';
import { Breadcrumbs } from '../components/EntityHeader';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Spinner } from '../components/ui/Spinner';

type Kind = 'all' | SearchResult['kind'];
const KINDS: Kind[] = ['all', 'customer', 'site', 'supplier', 'document'];

/** Full results page. The query itself lives in the top-bar search box. */
export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const kindParam = params.get('kind');
  const kind: Kind = KINDS.includes(kindParam as Kind) ? (kindParam as Kind) : 'all';

  const query = useApiQuery((api, signal) => api.search(q, signal), [q], { enabled: q.length > 0 });
  const all = useMemo(() => query.data?.results ?? [], [query.data]);
  const counts = useMemo(() => {
    const c: Record<Kind, number> = { all: all.length, customer: 0, site: 0, supplier: 0, document: 0 };
    for (const r of all) c[r.kind] += 1;
    return c;
  }, [all]);
  const results = kind === 'all' ? all : all.filter((r) => r.kind === kind);

  const setKind = (next: Kind) => {
    const p = new URLSearchParams(params);
    if (next === 'all') p.delete('kind');
    else p.set('kind', next);
    setParams(p, { replace: true });
    if (next !== 'document') writeSearchScope(next as SearchScope);
  };

  // Results-only page (reached by pressing Enter in the top-bar box); with no query there is nothing to show.
  if (q.length === 0) return <Navigate to="/" replace />;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <Breadcrumbs items={[{ label: 'Search' }]} />
        <h1 className="mt-2 text-4xl leading-none">Results for “{q}”</h1>
      </div>

      {query.loading ? (
        <Spinner label={`Searching for “${q}”`} />
      ) : query.error ? (
        <ErrorBanner title="Search failed" error={query.error} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <SearchScopeToggle value={kind === 'document' ? 'all' : kind} onChange={(s) => setKind(s)} counts={counts} />
            <div className="gsl-seg" role="group" aria-label="Documents">
              <button type="button" aria-pressed={kind === 'document'} onClick={() => setKind(kind === 'document' ? 'all' : 'document')} disabled={counts.document === 0}>
                Documents
                <span className="gsl-num opacity-70">{counts.document}</span>
              </button>
            </div>
          </div>
          <p className="sr-only" role="status">
            {results.length} result{results.length === 1 ? '' : 's'} for “{q}”
          </p>
          <SearchResults results={results} query={q} />
        </>
      )}
    </div>
  );
}
